import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import { type ContactDraft } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type ContactDisplay } from '#app/routes/mocra+/_form+/contacts/reference.ts'
import { type IncidentTeamStageKey } from '#app/routes/mocra+/_form+/recall-preparedness.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type IncidentTeamContactItem = {
	readonly display: ContactDisplay
	readonly errors: readonly string[]
}

type IncidentTeamStageView = {
	readonly key: IncidentTeamStageKey
	readonly question: string
	readonly description: string
	readonly defaultName: string
	readonly defaultTitle: string
	readonly directorySource: string
	readonly contacts: ReadonlyArray<IncidentTeamContactItem>
	readonly existingContacts: ReadonlyArray<ContactDraft>
	readonly listErrors: readonly string[]
}

type PrimaryCoordinatorView = {
	readonly display: ContactDisplay | null
	readonly errors: readonly string[]
	readonly hasCoordinator: boolean
}

type Props = {
	readonly resources: SectionResources
	readonly stages: ReadonlyArray<IncidentTeamStageView>
	readonly onAddContact: (
		stage: IncidentTeamStageKey,
		draft: ContactDraft,
	) => void
	readonly onEditContact: (stage: IncidentTeamStageKey, index: number) => void
	readonly onRemoveContact: (stage: IncidentTeamStageKey, index: number) => void
	readonly onAddPrimaryCoordinator: (draft: ContactDraft) => void
	readonly onEditPrimaryCoordinator: () => void
	readonly onClearPrimaryCoordinator: () => void
	readonly primaryCoordinator: PrimaryCoordinatorView
	readonly primaryCoordinatorDirectorySource?: string
}

export function IncidentTeamSection({
	resources,
	stages,
	onAddContact,
	onEditContact,
	onRemoveContact,
	onAddPrimaryCoordinator,
	onEditPrimaryCoordinator,
	onClearPrimaryCoordinator,
	primaryCoordinator,
	primaryCoordinatorDirectorySource,
}: Props) {
	return (
		<Section
			title="Incident response team"
			description="Capture the people who run the recall playbook—activation leads, regulatory owners, supply chain, and communications."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
			childrenClassName="space-y-6"
		>
			<div className="space-y-6">
				{stages.map((stage) => (
					<div key={stage.key} className="space-y-3">
						<div className="space-y-1">
							<p className="text-sm font-medium">{stage.question}</p>
							<p className="text-muted-foreground text-sm">
								{stage.description}
							</p>
						</div>
						{stage.listErrors.length ? (
							<ul className="text-destructive list-disc space-y-1 pl-5 text-xs">
								{stage.listErrors.map((error) => (
									<li key={error}>{error}</li>
								))}
							</ul>
						) : null}
						{stage.contacts.length > 0 ? (
							<div className="space-y-2">
								{stage.contacts.map((contact, index) => (
									<div key={`${stage.key}-${index}`} className="space-y-1">
										<ContactCard
											name={contact.display.name}
											title={contact.display.title}
											email={contact.display.email}
											phone={contact.display.phone}
											onClick={() => onEditContact(stage.key, index)}
											onRemove={() => onRemoveContact(stage.key, index)}
											ariaLabel={`${stage.defaultName} contact ${index + 1}`}
											className={
												contact.errors.length > 0
													? 'border-destructive/40 ring-destructive/40 ring-1'
													: undefined
											}
										/>
										{contact.errors.length > 0 ? (
											<p className="text-destructive text-sm">
												{contact.errors[0]}
											</p>
										) : null}
									</div>
								))}
							</div>
						) : null}
						<ContactAdd
							onSubmit={(draft) => onAddContact(stage.key, draft)}
							directorySource={stage.directorySource}
							existingContacts={stage.existingContacts}
						/>
					</div>
				))}
			</div>

			<div className="space-y-3">
				<div className="space-y-1">
					<p className="text-sm font-medium">
						Who is the primary recall coordinator?
					</p>
					<p className="text-muted-foreground text-sm">
						Select the single contact who has authority to coordinate the recall
						end-to-end.
					</p>
				</div>
				{primaryCoordinator.hasCoordinator && primaryCoordinator.display ? (
					<ContactCard
						name={primaryCoordinator.display.name}
						title={primaryCoordinator.display.title}
						email={primaryCoordinator.display.email}
						phone={primaryCoordinator.display.phone}
						onClick={onEditPrimaryCoordinator}
						onRemove={onClearPrimaryCoordinator}
						ariaLabel="Primary recall coordinator"
						removeLabel="Remove coordinator"
						removeConfirmTitle="Remove this coordinator?"
						removeConfirmDescription="This coordinator will be cleared from the form but remain available in your directory."
					/>
				) : (
					<ContactAdd
						onSubmit={onAddPrimaryCoordinator}
						triggerLabel="Add contact"
						directorySource={primaryCoordinatorDirectorySource}
					/>
				)}
				{primaryCoordinator.errors.length > 0 ? (
					<p className="text-destructive text-sm">
						{primaryCoordinator.errors[0]}
					</p>
				) : null}
			</div>
		</Section>
	)
}
